import React, { useEffect, useMemo, useState } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  getAnalytics,
  getDropOffAnalytics,
  getSlaAnalytics,
} from "../../api/adminApi";

export default function AnalyticsVisuals() {
  const [analytics, setAnalytics] = useState(null);
  const [sla, setSla] = useState(null);
  const [dropOffs, setDropOffs] = useState([]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [analyticsRes, slaRes, dropOffRes] = await Promise.all([
        getAnalytics(),
        getSlaAnalytics(),
        getDropOffAnalytics(),
      ]);

      setAnalytics(analyticsRes.data);
      setSla(slaRes.data);
      setDropOffs(dropOffRes.data || []);
    } catch (e) {
      console.error("Failed to load analytics visuals", e);
    }
  };

  const statusChartData = useMemo(() => {
    if (!analytics) return [];

    return [
      { label: "Total", value: analytics.totalDocuments || 0 },
      { label: "Active", value: analytics.activeDocuments || 0 },
      { label: "Completed", value: analytics.completedDocuments || 0 },
      { label: "Expired", value: analytics.expiredDocuments || 0 },
    ];
  }, [analytics]);

  const dropOffSummary = useMemo(() => {
    const summary = {
      PENDING: 0,
      VIEWED: 0,
      OTHER: 0,
    };

    (dropOffs || []).forEach((item) => {
      if (item.status === "PENDING") summary.PENDING += 1;
      else if (item.status === "VIEWED") summary.VIEWED += 1;
      else summary.OTHER += 1;
    });

    return summary;
  }, [dropOffs]);

  const dropOffPieData = useMemo(
    () => [
      { id: 0, label: "Pending", value: dropOffSummary.PENDING },
      { id: 1, label: "Viewed", value: dropOffSummary.VIEWED },
      { id: 2, label: "Other", value: dropOffSummary.OTHER },
    ],
    [dropOffSummary],
  );

  const nonZeroDropOffSlices = useMemo(
    () => dropOffPieData.filter((item) => item.value > 0).length,
    [dropOffPieData],
  );

  if (!analytics || !sla) return null;

  return (
    <div className="analytics-visual-grid">
      <div className="analytics-visual-panel">
        <h3 className="card-title">Document Status Overview</h3>
        <p className="card-subtitle">
          Visual comparison of total, active, completed, and expired documents.
        </p>

        <div className="analytics-chart-wrap">
          <BarChart
            dataset={statusChartData}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "label",
              },
            ]}
            series={[
              {
                dataKey: "value",
                label: "Documents",
                color: "#1c88bd",
              },
            ]}
            yAxis={[
              {
                min: 0,
                tickMinStep: 1,
              },
            ]}
            height={280}
            hideLegend
          />
        </div>
      </div>

      <div className="analytics-visual-panel">
        <h3 className="card-title">SLA Performance Snapshot</h3>
        <p className="card-subtitle">
          Completion speed across documents and signers.
        </p>

        <div className="kpi-chart-grid">
          <div className="kpi-chart-card">
            <div className="kpi-chart-label">Avg Document Completion</div>
            <div className="kpi-chart-value">
              {Number(sla.avgHoursToCompleteDocument || 0).toFixed(1)}h
            </div>
          </div>

          <div className="kpi-chart-card">
            <div className="kpi-chart-label">Avg Signer Completion</div>
            <div className="kpi-chart-value">
              {Number(sla.avgHoursToCompleteSigner || 0).toFixed(1)}h
            </div>
          </div>

          <div className="kpi-chart-card">
            <div className="kpi-chart-label">Fastest Document</div>
            <div className="kpi-chart-doc-name">
              {sla.fastestDocumentName || "-"}
            </div>
            <div className="kpi-chart-subvalue">
              {Number(sla.fastestDocumentHours || 0).toFixed(1)}h
            </div>
          </div>

          <div className="kpi-chart-card">
            <div className="kpi-chart-label">Slowest Document</div>
            <div className="kpi-chart-doc-name">
              {sla.slowestDocumentName || "-"}
            </div>
            <div className="kpi-chart-subvalue">
              {Number(sla.slowestDocumentHours || 0).toFixed(1)}h
            </div>
          </div>
        </div>
      </div>

      <div className="analytics-visual-panel analytics-visual-wide">
        <h3 className="card-title">Signer Drop-off Breakdown</h3>
        <p className="card-subtitle">
          Signers who opened documents but have not completed signing.
        </p>

        <div className="analytics-chart-wrap">
          <PieChart
            series={[
              {
                data: dropOffPieData,
                innerRadius: 36,
                outerRadius: 110,
                paddingAngle: nonZeroDropOffSlices > 1 ? 3 : 0,
                cornerRadius: nonZeroDropOffSlices > 1 ? 4 : 0,
              },
            ]}
            colors={["#f59e0b", "#1c88bd", "#64748b"]}
            height={280}
            margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
          />
        </div>

        <div className="dropoff-summary-strip">
          <div className="dropoff-summary-pill">
            Total Drop-offs: <strong>{dropOffs.length}</strong>
          </div>
          <div className="dropoff-summary-pill">
            Avg Views Before Drop-off:{" "}
            <strong>
              {dropOffs.length
                ? (
                    dropOffs.reduce(
                      (sum, item) => sum + (item.viewCount || 0),
                      0,
                    ) / dropOffs.length
                  ).toFixed(1)
                : "0.0"}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}
